import { useEffect, useState } from "react";
import { doc, onSnapshot, collection, query, where } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import { S, C } from "../ui";
import WorkersPanel from "./WorkersPanel";
import ScorecardPanel from "./ScorecardPanel";
import ReportPanel from "./ReportPanel";
import MessageThread from "./MessageThread";
import EntityDetailPanel from "./EntityDetailPanel";
import { VendorWorkOrdersTab } from "./WorkOrdersPanel";
import ScrollHint from "../core/ScrollHint";
import lockupLight from "../assets/lockup-light.svg";

export default function VendorAdminDashboard({ membership }) {
  const [tab, setTab] = useState("workers");
  const [facility, setFacility] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);

  useEffect(() => onSnapshot(doc(db, "facilities", membership.facilityId), s => setFacility(s.exists() ? s.data() : null)), [membership.facilityId]);
  useEffect(() => onSnapshot(doc(db, "vendors", membership.vendorId), s => setVendor(s.exists() ? s.data() : null)), [membership.vendorId]);
  useEffect(() => onSnapshot(query(collection(db, "vendorContracts"), where("vendorId", "==", membership.vendorId)), s =>
    setContracts(s.docs.map(d => ({ id: d.id, ...d.data() })))), [membership.vendorId]);

  if (!facility || !vendor) return <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted }}>Loading…</div>;

  return (
    <div style={S.page}>
      <div style={S.header} className="app-header">
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <img src={lockupLight} alt="Edifice View" style={{ height: 26, flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 700 }}>{vendor.companyName}</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,.55)" }}>{vendor.trade} · at {facility.name}</div>
          </div>
        </div>
        <button style={{ ...S.ghostBtn, width: "auto", padding: "8px 14px", color: C.mint, borderColor: C.mintDeep }} onClick={() => signOut(auth)}>Sign Out</button>
      </div>

      <ScrollHint className="app-tabbar" style={{ display: "flex", gap: 4, padding: "0 24px", background: C.white, borderBottom: `1px solid ${C.border}` }} fadeColor={C.white}>
        {[["workers", "Your Crew"], ["workorders", "Work Orders"], ["scorecard", "Your Scorecard"], ["reports", "Export"], ["contract", "Contract"], ["messages", "Messages"]].map(([id, label]) => (
          <div key={id} onClick={() => setTab(id)}
            style={{ padding: "12px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600, color: tab === id ? C.mintDeep : C.muted, borderBottom: tab === id ? `2px solid ${C.mint}` : "2px solid transparent" }}>
            {label}
          </div>
        ))}
      </ScrollHint>

      <div style={S.body} className="app-body">
        {tab === "workers" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, alignItems: "start" }} className="two-col-grid">
            <WorkersPanel facilityId={membership.facilityId} vendorId={membership.vendorId} roleType="vendor_staff" fixedTrade={vendor.trade} onSelect={setSelectedWorker} selectedId={selectedWorker?.id} />
            <div style={{ background: C.white, borderRadius: 0, border: `1px solid ${C.border}`, padding: 20, minHeight: 200 }}>
              {!selectedWorker ? (
                <div style={{ color: C.muted, fontSize: 13, textAlign: "center", paddingTop: 40 }}>Select a crew member to view their activity and schedule.</div>
              ) : (
                <EntityDetailPanel
                  facilityId={membership.facilityId} scopeType="worker" scopeId={selectedWorker.id} vendorId={membership.vendorId}
                  label={selectedWorker.name} sub={selectedWorker.trade}
                  viewerRole="vendor_admin" viewerName={vendor.companyName}
                />
              )}
            </div>
          </div>
        )}
        {tab === "workorders" && (
          <VendorWorkOrdersTab facilityId={membership.facilityId} facilityName={facility.name} vendorId={membership.vendorId} vendorName={vendor.companyName} />
        )}
        {tab === "scorecard" && (
          <ScorecardPanel facilityId={membership.facilityId} vendorId={membership.vendorId} vendors={[]} />
        )}
        {tab === "reports" && (
          <ReportPanel facilityId={membership.facilityId} vendorId={membership.vendorId} facilityName={facility.name} />
        )}
        {tab === "contract" && (
          <div style={{ background: C.white, borderRadius: 0, border: `1px solid ${C.border}`, padding: 20, maxWidth: 480 }}>
            <div style={{ ...S.kicker, marginBottom: 6 }}>Your contract at this facility</div>
            {contracts.map(c => (
              <div key={c.id} style={{ fontSize: 13, padding: "6px 0", borderBottom: `1px solid ${C.border}` }}>
                {c.startDate} → {c.endDate || "present"}
              </div>
            ))}
            <div style={{ marginTop: 20, padding: 16, background: C.bg, borderRadius: 0, fontSize: 13, color: C.muted }}>
              You'll only ever see your own company's data here, even
              though other vendors work in the same building (enforced
              server-side by Firestore Security Rules, not just hidden in
              the UI — verified directly against the database, not just
              observed in this app).
            </div>
          </div>
        )}
        {tab === "messages" && (
          <div style={{ background: C.white, borderRadius: 0, border: `1px solid ${C.border}`, padding: 20, maxWidth: 640, minHeight: 460 }}>
            <MessageThread facilityId={membership.facilityId} threadType="vendor" threadId={membership.vendorId} senderRole="vendor_admin" senderName={vendor.companyName} />
          </div>
        )}
      </div>
    </div>
  );
}
